'use client'

import { useApi } from '@repo/api'
import {
  getImpersonationRecovery,
  getImpersonationRecoveryRemainingSeconds,
  getImpersonationRecoveryTargetLabel,
  getImpersonatorId,
  saveImpersonationRecovery,
  useAuth,
} from '@repo/auth'
import { useIsMutating, useMutation, useQueryClient } from '@repo/tanstack/query'
import { toast } from '@repo/ui/components'
import { useRouter } from 'next/navigation'
import { useEffect, useRef, useState } from 'react'

const START_IMPERSONATION_MUTATION_KEY = ['impersonation', 'start'] as const
const STOP_IMPERSONATION_MUTATION_KEY = ['impersonation', 'stop'] as const

type ImpersonationTarget = {
  email: string
  id: number
  name: string
}

export const useImpersonation = () => {
  const {
    getSessionVersion,
    impersonatorId,
    isImpersonating,
    replaceAuthToken,
    restoreImpersonationSession,
    token,
    user,
  } = useAuth()
  const api = useApi()
  const queryClient = useQueryClient()
  const router = useRouter()

  const [remainingSeconds, setRemainingSeconds] = useState<null | number>(null)
  const [targetLabel, setTargetLabel] = useState<null | string>(null)
  const expiryIntervalRef = useRef<null | ReturnType<typeof setInterval>>(null)

  useEffect(() => {
    const tick = () => {
      const recovery = getImpersonationRecovery()
      if (!recovery) {
        setRemainingSeconds(null)
        setTargetLabel(null)
        return
      }

      if (!isImpersonating || !token) {
        setRemainingSeconds(null)
        setTargetLabel(null)
        if (expiryIntervalRef.current) {
          clearInterval(expiryIntervalRef.current)
          expiryIntervalRef.current = null
        }
        return
      }

      const currentImpersonatorId = getImpersonatorId(token)
      if (String(currentImpersonatorId) !== recovery.adminUserId) {
        setRemainingSeconds(null)
        setTargetLabel(null)
        if (expiryIntervalRef.current) {
          clearInterval(expiryIntervalRef.current)
          expiryIntervalRef.current = null
        }
        return
      }

      const seconds = getImpersonationRecoveryRemainingSeconds(recovery)
      setRemainingSeconds(seconds)
      setTargetLabel(getImpersonationRecoveryTargetLabel(recovery))

      if (seconds <= 0) {
        if (expiryIntervalRef.current) {
          clearInterval(expiryIntervalRef.current)
          expiryIntervalRef.current = null
        }
        const restoration = restoreImpersonationSession()
        const recoveryVersion = getSessionVersion()
        restoration.then(restored => {
          if (getSessionVersion() !== recoveryVersion) return
          if (restored) {
            toast.success('Impersonation expired. Restored your admin session.')
            router.refresh()
          } else {
            toast.error('Impersonation expired. Could not restore your admin session.')
            router.push('/')
          }
        })
      }
    }

    tick()
    expiryIntervalRef.current = setInterval(tick, 1000)

    return () => {
      if (expiryIntervalRef.current) {
        clearInterval(expiryIntervalRef.current)
        expiryIntervalRef.current = null
      }
    }
  }, [getSessionVersion, isImpersonating, restoreImpersonationSession, router, token])

  const handleStopError = () => {
    toast.error('Failed to exit impersonation. Please try again or sign out manually.')
  }

  const start = useMutation({
    mutationFn: (target: ImpersonationTarget) =>
      api.auth.impersonate.start.mutate({ path: { userId: target.id } }),
    mutationKey: START_IMPERSONATION_MUTATION_KEY,
    onError: () => {
      toast.error('Failed to start impersonation. The user may not be eligible.')
    },
    onSuccess: async (data, target) => {
      const adminToken = token
      if (!adminToken || !user) {
        toast.error('Failed to save impersonation recovery. Please try again.')
        return
      }

      const saved = saveImpersonationRecovery({
        adminToken,
        adminUserId: user.id,
        impersonationToken: data.access_token,
        targetUser: {
          email: target.email,
          id: target.id,
          name: target.name,
        },
      })

      if (!saved) {
        toast.error('Failed to save impersonation recovery. Please try again.')
        return
      }

      await replaceAuthToken(data.access_token, data.expires_in)
      queryClient.clear()
      router.push('/')
      router.refresh()
      toast.success(
        `You are now impersonating ${target.name} (${target.email}). Use the banner to exit.`,
      )
    },
  })

  const stop = useMutation({
    mutationFn: () => api.auth.impersonate.stop.mutate(),
    mutationKey: STOP_IMPERSONATION_MUTATION_KEY,
    onError: handleStopError,
    onSuccess: async data => {
      if (getImpersonatorId(data.access_token) !== null) {
        handleStopError()
        return
      }

      await replaceAuthToken(data.access_token, data.expires_in)
      queryClient.clear()
      router.refresh()
      toast.success('Impersonation ended. You are back to your admin session.')
    },
  })

  const startPending = useIsMutating({ mutationKey: START_IMPERSONATION_MUTATION_KEY }) > 0

  return {
    impersonatorId,
    isImpersonating,
    remainingSeconds,
    restoreImpersonationSession,
    startImpersonation: start.mutate,
    startPending,
    stopImpersonation: stop.mutate,
    stopPending: stop.isPending,
    targetLabel,
    user,
  }
}
